import type { Friend } from '@/types/friendship';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React from 'react';
import { ActivityIndicator, Image, Pressable, Text, View } from 'react-native';
import { useAppTheme } from '@/providers/theme-provider';

interface FriendsListProps {
  friends: Friend[];
  isLoading: boolean;
}

export function FriendsList({ friends, isLoading }: FriendsListProps) {
  const theme = useAppTheme();
  if (isLoading) {
    return (
      <View className="py-8 items-center">
        <ActivityIndicator color={theme.colors.primary} />
      </View>
    );
  }

  if (friends.length === 0) {
    return (
      <View className="items-center py-8">
        <View className="w-16 h-16 rounded-full items-center justify-center mb-4 bg-surface-secondary">
          <Ionicons
            name="people-outline"
            size={28}
            className="text-muted-foreground"
          />
        </View>
        <Text className="text-center text-muted-foreground">
          No friends yet
        </Text>
      </View>
    );
  }

  return (
    <View>
      {friends.map((friend) => {
        const displayName = friend.user.name || 'Unknown';
        const rawHandle = friend.user.username || friend.user.displayUsername || '';
        const normalizedHandle = rawHandle.replace(/^@+/, '');
        const handle = normalizedHandle ? `@${normalizedHandle}` : '';
        const avatarUri = imageUrl(friend.user.image || friend.user.avatar, 'thumbnail');

        return (
          <Pressable
            key={friend.id}
            className="flex-row items-center rounded-xl p-3 mb-2 bg-surface"
            onPress={() => {
              const handle = (friend.user.username || friend.user.displayUsername || '').replace(/^@+/, '');
              router.push({ pathname: '/(tabs)/profile/[username]', params: { username: handle || friend.friendId } });
            }}
          >
            <View
              className="items-center justify-center mr-3 bg-surface-secondary border border-border"
              style={{
                width: 48,
                height: 48,
                borderRadius: 24,
                overflow: 'hidden',
              }}
            >
              {avatarUri ? (
                <Image
                  source={{ uri: avatarUri }}
                  style={{ width: 48, height: 48, borderRadius: 24 }}
                  resizeMode="cover"
                  accessibilityLabel={`${displayName}'s profile photo`}
                />
              ) : (
                <Ionicons
                  name="person-circle"
                  size={38}
                  className="text-muted-foreground"
                />
              )}
            </View>
            <View className="flex-1">
              <Text className="font-medium text-base text-foreground">
                {displayName}
              </Text>
              {handle ? (
                <Text className="text-sm text-muted-foreground">
                  {handle}
                </Text>
              ) : null}
              {(friend.friendCount != null || friend.checkInCount != null) && (
                <Text className="text-xs text-muted-foreground mt-px">
                  {[
                    friend.friendCount != null
                      ? `${friend.friendCount} ${friend.friendCount === 1 ? 'friend' : 'friends'}${friend.mutualFriendCount ? ` (${friend.mutualFriendCount} mutual)` : ''}`
                      : null,
                    friend.checkInCount != null
                      ? `${friend.checkInCount} ${friend.checkInCount === 1 ? 'check-in' : 'check-ins'}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join('  \u00B7  ')}
                </Text>
              )}
            </View>
            <Ionicons
              name="chevron-forward"
              size={16}
              className="text-muted-foreground"
            />
          </Pressable>
        );
      })}
    </View>
  );
}
